import React, { useCallback, useMemo } from "react";
import isHotkey from "is-hotkey";
import { Editable, withReact, useSlate, Slate } from "slate-react";
import {
  Editor,
  Transforms,
  createEditor,
  Element as SlateElement,
  Text,
  Path
} from "slate";
import { withHistory } from "slate-history";
import { Button, Icon, Toolbar } from "./Components2";
import escapeHtml from "escape-html";
import PropTypes from "prop-types";

const HOTKEYS = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
  "mod+`": "code"
};

const LIST_TYPES = ["numbered-list", "bulleted-list"];
const TEXT_ALIGN_TYPES = ["left", "center", "right", "justify"];

// Normalizer: Ensure there is always an empty paragraph below a table
const withTables = editor => {
  const { normalizeNode } = editor;
  editor.normalizeNode = entry => {
    const [, path] = entry;
    if (path.length === 0) {
      if (editor.children.length > 0) {
        const lastNode = editor.children[editor.children.length - 1];
        if (lastNode.type === "table") {
          Transforms.insertNodes(
            editor,
            { type: "paragraph", children: [{ text: "" }] },
            { at: [editor.children.length] }
          );
          return;
        }
      }
    }
    normalizeNode(entry);
  };
  return editor;
};

const RichTextComponent = ({
  onChange,
  value,
  isCreate = false,
  disable = false
}) => {
  const renderElement = useCallback(props => <Element {...props} />, []);
  const renderLeaf = useCallback(props => <Leaf {...props} />, []);

  const editor = useMemo(
    () => withTables(withHistory(withReact(createEditor()))),
    []
  );

  const serialize = node => {
    if (Text.isText(node)) {
      let string = escapeHtml(node.text).replace(/\n/g, "<br />");
      if (node.underline) {
        string = `<u>${string}</u>`;
      }
      if (node.italic) {
        string = `<i>${string}</i>`;
      }
      if (node.bold) {
        string = `<strong>${string}</strong>`;
      }
      return string;
    }

    const children = node.children.map(n => serialize(n)).join("");

    switch (node.type) {
      case "quote":
        return `<blockquote><p>${children}</p></blockquote>`;
      case "paragraph": {
        let str = "";
        // PERBAIKAN: Menghapus margin default pada tag p agar super rapat
        if (node.align) {
          str = `<p style="margin: 0; text-align: ${node.align};">${children}</p>`;
        } else {
          str = `<p style="margin: 0;">${children}</p>`;
        }
        return str;
      }
      case "bulleted-list":
        return `<ul style="margin-top: 4px; margin-bottom: 4px; padding-left: 20px;">${children}</ul>`;
      case "numbered-list":
        return `<ol style="margin-top: 4px; margin-bottom: 4px; padding-left: 20px;">${children}</ol>`;
      case "heading-one": {
        let strH1 = "";
        if (node.align) {
          strH1 = `<h1 style="text-align: ${node.align};">${children}</h1>`;
        } else {
          strH1 = `<h1>${children}</h1>`;
        }
        return strH1;
      }
      case "heading-two": {
        let strH2 = "";
        if (node.align) {
          strH2 = `<h2 style="text-align: ${node.align};">${children}</h2>`;
        } else {
          strH2 = `<h2>${children}</h2>`;
        }
        return strH2;
      }
      case "link":
        return `<a href="${escapeHtml(node.url)}">${children}</a>`;
      case "list-item":
        return `<li style="margin-top: 2px; margin-bottom: 2px;">${children}</li>`;
      case "table": {
        const borderStyle = node.isBorderless
          ? "border: none;"
          : "border: 1px solid #ccc;";
        const tableWidth = node.tableWidth || "100%";
        return `<table style="width: ${tableWidth}; border-collapse: collapse; ${borderStyle} margin-bottom: 10px;"><tbody>${children}</tbody></table>`;
      }
      case "table-row": {
        const bgColor = node.bgColor
          ? `background-color: ${node.bgColor};`
          : "";
        const fontWeight = node.isHeader ? "font-weight: bold;" : "";
        return `<tr style="${bgColor} ${fontWeight}">${children}</tr>`;
      }
      case "table-cell": {
        const borderStyle = node.isBorderless
          ? "border: none;"
          : "border: 1px solid #ccc;";
        // PERBAIKAN: Mengubah padding atas bawah jadi 0px untuk list tanpa border
        const paddingStyle = node.isBorderless
          ? "padding: 0px 8px 0px 0;"
          : "padding: 8px;";
        const widthStyle = node.colWidth ? `width: ${node.colWidth};` : "";
        return `<td style="${borderStyle} ${paddingStyle} ${widthStyle} vertical-align: top;">${children}</td>`;
      }
      default:
        return children;
    }
  };

  const onChangeText = data => {
    let a = {
      children: null
    };
    a.children = data;
    const dataSerialize = serialize(a);
    onChange(dataSerialize);
  };

  return (
    <div style={{ borderWidth: "1px", borderColor: "#eee" }}>
      <Slate
        editor={editor}
        initialValue={isCreate ? initialValue : value ?? initialValue}
        onChange={onChangeText}
      >
        <Toolbar>
          <MarkButton format="bold" icon="format_bold" />
          <MarkButton format="italic" icon="format_italic" />
          <MarkButton format="underline" icon="format_underlined" />
          <BlockButton format="heading-one" icon="looks_one" />
          <BlockButton format="heading-two" icon="looks_two" />
          <BlockButton format="block-quote" icon="format_quote" />
          <BlockButton format="bulleted-list" icon="format_list_bulleted" />
          <BlockButton format="numbered-list" icon="format_list_numbered" />
          <BlockButton format="left" icon="format_align_left" />
          <BlockButton format="center" icon="format_align_center" />
          <BlockButton format="right" icon="format_align_right" />
          <BlockButton format="justify" icon="format_align_justify" />
          {/* Table & Template Creation */}
          <TableButton icon="table_chart" />
          <AlignedListButton icon="view_list" />
          <PromoStepsButton icon="assignment_turned_in" />
          <ScheduleTemplateButton icon="event_note" />
          {/* Row Manipulations */}
          <AddRowAboveButton icon="arrow_upward" />
          <AddRowBelowButton icon="arrow_downward" />
          <RemoveRowButton icon="remove_circle" />
          {/* Column Manipulations */}
          <AddColButton icon="add_box" />
          <RemoveColButton icon="delete" />
          {/* Table Width & Column Width adjustments */}
          <ColWidthDecreaseButton icon="chevron_left" />
          <ColWidthSelect />
          <ColWidthIncreaseButton icon="chevron_right" />
          <TableWidthSelect />
          {/* Styling & Coloring */}
          <TableHeaderButton icon="title" />
          <TableColorPicker />
        </Toolbar>
        <Editable
          readOnly={disable}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          placeholder="Enter some rich text…"
          spellCheck
          autoFocus
          style={{ minHeight: 200 }}
          onKeyDown={event => {
            if (event.key === "Enter" && event.shiftKey) {
              event.preventDefault();
              editor.insertText("\n");
              return;
            }
            for (const hotkey in HOTKEYS) {
              if (isHotkey(hotkey, event)) {
                event.preventDefault();
                const mark = HOTKEYS[hotkey];
                toggleMark(editor, mark);
              }
            }
          }}
        />
      </Slate>
    </div>
  );
};

// Insert standard bordered table
const insertTable = (editor, rows = 3, cols = 3) => {
  const table = {
    type: "table",
    isBorderless: false,
    tableWidth: "100%",
    children: Array.from({ length: rows }, (_, rowIndex) => ({
      type: "table-row",
      isHeader: rowIndex === 0,
      bgColor: rowIndex === 0 ? "#E6F0F8" : "transparent",
      children: Array.from({ length: cols }, () => ({
        type: "table-cell",
        isBorderless: false,
        colWidth: "33%",
        children: [{ type: "paragraph", children: [{ text: "" }] }]
      }))
    }))
  };

  Transforms.insertNodes(editor, [
    table,
    { type: "paragraph", children: [{ text: "" }] }
  ]);
};

// Insert basic 1-row aligned list
const insertAlignedList = editor => {
  const rowsCount = 1;
  const table = {
    type: "table",
    isBorderless: true,
    tableWidth: "100%",
    children: Array.from({ length: rowsCount }, () => ({
      type: "table-row",
      isHeader: false,
      bgColor: "transparent",
      children: [
        {
          type: "table-cell",
          isBorderless: true,
          colWidth: "30%",
          children: [{ type: "paragraph", children: [{ text: "" }] }]
        },
        {
          type: "table-cell",
          isBorderless: true,
          colWidth: "5%",
          children: [{ type: "paragraph", children: [{ text: ":" }] }]
        },
        {
          type: "table-cell",
          isBorderless: true,
          colWidth: "65%",
          children: [{ type: "paragraph", children: [{ text: "" }] }]
        }
      ]
    }))
  };

  Transforms.insertNodes(editor, [
    table,
    { type: "paragraph", children: [{ text: "" }] }
  ]);
};

// Insert Shuttle Schedule Template (Based on User's Image)
const insertScheduleTemplate = editor => {
  const scheduleData = [
    [
      { text: "First pick-up", bold: true },
      { text: ":" },
      { text: "9:40 AM (Batam Time)" }
    ],
    [
      { text: "Starting point", bold: true },
      { text: ":" },
      { text: "HARRIS Hotel Batam Center (right next to the terminal)." }
    ],
    [
      { text: "Available", bold: true },
      { text: ":" },
      { text: "Every weekend" }
    ],
    [{ text: "" }, { text: "" }, { text: "" }],
    [
      { text: "Shuttle Schedule", bold: true },
      { text: "" },
      { text: "(Batam Time)" }
    ],
    [{ text: "Session 1 :", bold: true }, { text: "" }, { text: "" }],
    [
      { text: "Route", bold: true },
      { text: ":" },
      { text: "HARRIS Hotel BTC* → Grand Mercure BTC* → One Batam Mall" }
    ],
    [
      { text: "Departure Time", bold: true },
      { text: ":" },
      { text: "9:40 AM → 9:50 AM → 10:00 AM" }
    ],
    [{ text: "" }, { text: "" }, { text: "" }],
    [{ text: "Session 2 :", bold: true }, { text: "" }, { text: "" }],
    [
      { text: "Route", bold: true },
      { text: ":" },
      { text: "HARRIS Hotel BTC* → Grand Mercure BTC* → One Batam Mall" }
    ],
    [
      { text: "Departure Time", bold: true },
      { text: ":" },
      { text: "11:00 AM → 11:10 AM → 11:20 AM" }
    ],
    [{ text: "" }, { text: "" }, { text: "" }],
    [{ text: "Session 3 :", bold: true }, { text: "" }, { text: "" }],
    [
      { text: "Route", bold: true },
      { text: ":" },
      { text: "HARRIS Hotel BTC* → Grand Mercure BTC* → One Batam Mall" }
    ],
    [
      { text: "Departure Time", bold: true },
      { text: ":" },
      { text: "1:00 PM → 1:10 PM → 1:20 PM" }
    ],
    [{ text: "" }, { text: "" }, { text: "" }],
    [{ text: "Session 4 :", bold: true }, { text: "" }, { text: "" }],
    [
      { text: "Route", bold: true },
      { text: ":" },
      { text: "One Batam Mall → BCS**" }
    ],
    [
      { text: "Departure Time", bold: true },
      { text: ":" },
      { text: "3:30 PM → 4:10 PM" }
    ]
  ];

  const table = {
    type: "table",
    isBorderless: true,
    tableWidth: "100%",
    children: scheduleData.map(row => ({
      type: "table-row",
      isHeader: false,
      bgColor: "transparent",
      children: [
        {
          type: "table-cell",
          isBorderless: true,
          colWidth: "25%",
          children: [{ type: "paragraph", children: [row[0]] }]
        },
        {
          type: "table-cell",
          isBorderless: true,
          colWidth: "5%",
          children: [{ type: "paragraph", children: [row[1]] }]
        },
        {
          type: "table-cell",
          isBorderless: true,
          colWidth: "70%",
          children: [{ type: "paragraph", children: [row[2]] }]
        }
      ]
    }))
  };

  Transforms.insertNodes(editor, [
    table,
    { type: "paragraph", children: [{ text: "" }] }
  ]);
};

// "3 Easy Steps" Promo Template
const insertPromoSteps = editor => {
  const titleNode = {
    type: "paragraph",
    children: [{ text: "3 Easy Steps to Claim Your FREE Ride!", bold: true }]
  };

  const stepsData = [
    {
      num: "1.",
      title: "Get the Sindo Ferry App 📲",
      desc:
        "Download the Sindo Ferry App, open Partner Promo, and claim your FREE shuttle ride voucher.\nPlease present your app voucher before boarding.\nNo app voucher, no boarding."
    },
    {
      num: "2.",
      title: "Chope Your Seat 💺",
      desc:
        "Seats are available on a first-come, first-served basis.\nWhatsApp us with your preferred schedule and number of passengers at least 15 minutes before departure:\n📲 +62 811-6663-061\nYour app voucher confirms your eligibility, while your seat is only secured after receiving confirmation via WhatsApp."
    },
    {
      num: "3.",
      title: "Be Ready to Go! ⏰",
      desc:
        "Please arrive at the designated pick-up point at least 5 minutes before departure.\nDeparture times may shift slightly depending on Batam traffic and operational conditions."
    }
  ];

  const table = {
    type: "table",
    isBorderless: true,
    tableWidth: "100%",
    children: stepsData.map(step => ({
      type: "table-row",
      isHeader: false,
      bgColor: "transparent",
      children: [
        {
          type: "table-cell",
          isBorderless: true,
          colWidth: "5%",
          children: [
            {
              type: "paragraph",
              children: [{ text: step.num, bold: true }]
            }
          ]
        },
        {
          type: "table-cell",
          isBorderless: true,
          colWidth: "95%",
          children: [
            {
              type: "paragraph",
              children: [{ text: step.title + "\n", bold: true }]
            },
            {
              type: "paragraph",
              children: [{ text: step.desc }]
            }
          ]
        }
      ]
    }))
  };

  Transforms.insertNodes(editor, [
    titleNode,
    table,
    { type: "paragraph", children: [{ text: "" }] }
  ]);
};

// Helper to construct a new row matching current table structure
const createNewRow = (editor, tableNode) => {
  const isBorderless = !!tableNode.isBorderless;
  const firstRow = tableNode.children[0];
  const colCount = firstRow ? firstRow.children.length : 3;

  return {
    type: "table-row",
    isHeader: false,
    bgColor: "transparent",
    children: Array.from({ length: colCount }, (_, colIndex) => {
      let width = "auto";
      let cellText = "";
      if (firstRow && firstRow.children[colIndex]) {
        width = firstRow.children[colIndex].colWidth || "auto";
      }

      if (isBorderless) {
        if (colCount === 3 && colIndex === 1 && width === "5%") {
          cellText = ":";
        }
        if (colCount === 2 && colIndex === 0 && width === "5%") {
          const nextNum = tableNode.children.length + 1;
          cellText = `${nextNum}.`;
        }
      }

      return {
        type: "table-cell",
        isBorderless: isBorderless,
        colWidth: width,
        children: [{ type: "paragraph", children: [{ text: cellText }] }]
      };
    })
  };
};

// Insert a new row BELOW the currently selected cell
const insertRowBelow = editor => {
  const [matchTable] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "table"
  });
  if (!matchTable) return;

  const [tableNode] = matchTable;
  const newRow = createNewRow(editor, tableNode);

  const [rowMatch] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "table-row"
  });

  if (rowMatch) {
    const [, rowPath] = rowMatch;
    const nextPath = Path.next(rowPath);
    Transforms.insertNodes(editor, newRow, { at: nextPath });
  } else {
    Transforms.insertNodes(editor, newRow);
  }
};

// Insert a new row ABOVE the currently selected cell
const insertRowAbove = editor => {
  const [matchTable] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "table"
  });
  if (!matchTable) return;

  const [tableNode] = matchTable;
  const newRow = createNewRow(editor, tableNode);

  const [rowMatch] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "table-row"
  });

  if (rowMatch) {
    const [, rowPath] = rowMatch;
    Transforms.insertNodes(editor, newRow, { at: rowPath });
  } else {
    Transforms.insertNodes(editor, newRow);
  }
};

// Remove the currently selected row
const removeRow = editor => {
  const [matchRow] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "table-row"
  });
  const [matchTable] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "table"
  });
  if (!matchRow || !matchTable) return;

  const [tableNode, tablePath] = matchTable;
  const [, rowPath] = matchRow;

  if (tableNode.children.length <= 1) {
    Transforms.removeNodes(editor, { at: tablePath });
  } else {
    Transforms.removeNodes(editor, { at: rowPath });
  }
};

// Insert a new column to the right of the currently selected cell
const insertColumn = editor => {
  const [matchCell] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      n.type === "table-cell"
  });
  const [matchTable] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "table"
  });
  if (!matchCell || !matchTable) return;

  const [tableNode, tablePath] = matchTable;
  const [, cellPath] = matchCell;
  const colIndex = cellPath[cellPath.length - 1];
  const isBorderless = !!tableNode.isBorderless;

  tableNode.children.forEach((_, rowIndex) => {
    const targetPath = [...tablePath, rowIndex, colIndex + 1];
    const newCell = {
      type: "table-cell",
      isBorderless: isBorderless,
      colWidth: "auto",
      children: [{ type: "paragraph", children: [{ text: "" }] }]
    };
    Transforms.insertNodes(editor, newCell, { at: targetPath });
  });
};

// Remove the currently selected column
const removeColumn = editor => {
  const [matchCell] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      n.type === "table-cell"
  });
  const [matchTable] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "table"
  });
  if (!matchCell || !matchTable) return;

  const [tableNode, tablePath] = matchTable;
  const [, cellPath] = matchCell;
  const colIndex = cellPath[cellPath.length - 1];

  const firstRow = tableNode.children[0];
  if (firstRow && firstRow.children.length <= 1) {
    Transforms.removeNodes(editor, { at: tablePath });
    return;
  }

  for (let r = tableNode.children.length - 1; r >= 0; r--) {
    const targetPath = [...tablePath, r, colIndex];
    Transforms.removeNodes(editor, { at: targetPath });
  }
};

// Set width for ALL cells in the currently selected vertical column
const setColumnWidth = (editor, newWidth) => {
  const [matchCell] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      n.type === "table-cell"
  });
  const [matchTable] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "table"
  });
  if (!matchCell || !matchTable) return;

  const [tableNode, tablePath] = matchTable;
  const [, cellPath] = matchCell;
  const colIndex = cellPath[cellPath.length - 1];

  tableNode.children.forEach((_, rowIndex) => {
    const targetPath = [...tablePath, rowIndex, colIndex];
    Transforms.setNodes(editor, { colWidth: newWidth }, { at: targetPath });
  });
};

// Step adjustment (+/- delta percentage) for current column
const adjustColumnWidth = (editor, delta) => {
  const [matchCell] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      n.type === "table-cell"
  });
  if (!matchCell) return;

  const [cellNode] = matchCell;
  let currentVal = parseInt(cellNode.colWidth, 10);
  if (isNaN(currentVal)) currentVal = 33;
  const newVal = Math.min(100, Math.max(5, currentVal + delta)) + "%";
  setColumnWidth(editor, newVal);
};

// Set total width for the currently selected table
const setTableWidth = (editor, newWidth) => {
  const [matchTable] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "table"
  });
  if (matchTable) {
    const [, path] = matchTable;
    Transforms.setNodes(editor, { tableWidth: newWidth }, { at: path });
  }
};

const setTableRowColor = (editor, color) => {
  const [match] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "table-row"
  });
  if (match) {
    const [, path] = match;
    Transforms.setNodes(editor, { bgColor: color }, { at: path });
  }
};

const toggleTableHeader = editor => {
  const [match] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "table-row"
  });
  if (match) {
    const [node, path] = match;
    Transforms.setNodes(editor, { isHeader: !node.isHeader }, { at: path });
  }
};

const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? "align" : "type"
  );
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true
  });
  let newProperties;
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format
    };
  } else {
    newProperties = {
      type: isActive ? "paragraph" : isList ? "list-item" : format
    };
  }
  Transforms.setNodes(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const isBlockActive = (editor, format, blockType = "type") => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n[blockType] === format
    })
  );

  return !!match;
};

const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

const Element = ({ attributes, children, element }) => {
  const style = { textAlign: element.align };
  switch (element.type) {
    case "block-quote":
      return (
        <blockquote style={style} {...attributes}>
          {children}
        </blockquote>
      );
    case "bulleted-list":
      return (
        <ul
          style={{
            marginTop: "4px",
            marginBottom: "4px",
            paddingLeft: "20px",
            ...style
          }}
          {...attributes}
        >
          {children}
        </ul>
      );
    case "heading-one":
      return (
        <h1 style={style} {...attributes}>
          {children}
        </h1>
      );
    case "heading-two":
      return (
        <h2 style={style} {...attributes}>
          {children}
        </h2>
      );
    case "list-item":
      return (
        <li
          style={{ marginTop: "2px", marginBottom: "2px", ...style }}
          {...attributes}
        >
          {children}
        </li>
      );
    case "numbered-list":
      return (
        <ol
          style={{
            marginTop: "4px",
            marginBottom: "4px",
            paddingLeft: "20px",
            ...style
          }}
          {...attributes}
        >
          {children}
        </ol>
      );
    case "table":
      return (
        <table
          style={{
            width: element.tableWidth || "100%",
            borderCollapse: "collapse",
            border: element.isBorderless ? "none" : "1px solid #ccc",
            marginBottom: "10px",
            ...style
          }}
          {...attributes}
        >
          <tbody>{children}</tbody>
        </table>
      );
    case "table-row":
      return (
        <tr
          style={{
            backgroundColor: element.bgColor || "transparent",
            fontWeight: element.isHeader ? "bold" : "normal",
            ...style
          }}
          {...attributes}
        >
          {children}
        </tr>
      );
    case "table-cell":
      return (
        <td
          style={{
            border: element.isBorderless
              ? "1px dashed #e0e0e0"
              : "1px solid #ccc",
            // PERBAIKAN: Padding 0px agar selnya super rapat tidak ada rongga kosong
            padding: element.isBorderless ? "0px 8px 0px 0" : "8px",
            width: element.colWidth || "auto",
            minWidth: "30px",
            verticalAlign: "top",
            ...style
          }}
          {...attributes}
        >
          {children}
        </td>
      );
    default:
      return (
        // PERBAIKAN: Menghapus margin bawaan paragraf agar tidak bikin row jadi tinggi
        <p style={{ margin: 0, ...style }} {...attributes}>
          {children}
        </p>
      );
  }
};

Element.propTypes = {
  attributes: PropTypes.any,
  children: PropTypes.any,
  element: PropTypes.any
};

const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.code) {
    children = <code>{children}</code>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  return <span {...attributes}>{children}</span>;
};

Leaf.propTypes = {
  attributes: PropTypes.any,
  children: PropTypes.any,
  leaf: PropTypes.any
};

const BlockButton = ({ format, icon }) => {
  const editor = useSlate();
  return (
    <Button
      active={isBlockActive(
        editor,
        format,
        TEXT_ALIGN_TYPES.includes(format) ? "align" : "type"
      )}
      onMouseDown={event => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

BlockButton.propTypes = {
  format: PropTypes.any,
  icon: PropTypes.any
};

const MarkButton = ({ format, icon }) => {
  const editor = useSlate();
  return (
    <Button
      active={isMarkActive(editor, format)}
      onMouseDown={event => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

MarkButton.propTypes = {
  format: PropTypes.any,
  icon: PropTypes.any
};

const TableButton = ({ icon }) => {
  const editor = useSlate();
  return (
    <Button
      title="Create Standard Table"
      onMouseDown={event => {
        event.preventDefault();
        insertTable(editor, 3, 3);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

TableButton.propTypes = {
  icon: PropTypes.any
};

const AlignedListButton = ({ icon }) => {
  const editor = useSlate();
  return (
    <Button
      title="Create Aligned Info List (1 Row)"
      onMouseDown={event => {
        event.preventDefault();
        insertAlignedList(editor);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

AlignedListButton.propTypes = {
  icon: PropTypes.any
};

const PromoStepsButton = ({ icon }) => {
  const editor = useSlate();
  return (
    <Button
      title="Insert 3 Easy Steps Promo Template"
      onMouseDown={event => {
        event.preventDefault();
        insertPromoSteps(editor);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

PromoStepsButton.propTypes = {
  icon: PropTypes.any
};

const ScheduleTemplateButton = ({ icon }) => {
  const editor = useSlate();
  return (
    <Button
      title="Insert Full Shuttle Schedule Template"
      onMouseDown={event => {
        event.preventDefault();
        insertScheduleTemplate(editor);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

ScheduleTemplateButton.propTypes = {
  icon: PropTypes.any
};

const AddRowAboveButton = ({ icon }) => {
  const editor = useSlate();
  return (
    <Button
      title="Insert Row Above"
      onMouseDown={event => {
        event.preventDefault();
        insertRowAbove(editor);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

AddRowAboveButton.propTypes = {
  icon: PropTypes.any
};

const AddRowBelowButton = ({ icon }) => {
  const editor = useSlate();
  return (
    <Button
      title="Insert Row Below"
      onMouseDown={event => {
        event.preventDefault();
        insertRowBelow(editor);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

AddRowBelowButton.propTypes = {
  icon: PropTypes.any
};

const RemoveRowButton = ({ icon }) => {
  const editor = useSlate();
  return (
    <Button
      title="Delete Selected Row"
      onMouseDown={event => {
        event.preventDefault();
        removeRow(editor);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

RemoveRowButton.propTypes = {
  icon: PropTypes.any
};

const AddColButton = ({ icon }) => {
  const editor = useSlate();
  return (
    <Button
      title="Insert Column Right"
      onMouseDown={event => {
        event.preventDefault();
        insertColumn(editor);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

AddColButton.propTypes = {
  icon: PropTypes.any
};

const RemoveColButton = ({ icon }) => {
  const editor = useSlate();
  return (
    <Button
      title="Delete Selected Column"
      onMouseDown={event => {
        event.preventDefault();
        removeColumn(editor);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

RemoveColButton.propTypes = {
  icon: PropTypes.any
};

const ColWidthDecreaseButton = ({ icon }) => {
  const editor = useSlate();
  return (
    <Button
      title="Decrease Column Width (-5%)"
      onMouseDown={event => {
        event.preventDefault();
        adjustColumnWidth(editor, -5);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

ColWidthDecreaseButton.propTypes = {
  icon: PropTypes.any
};

const ColWidthIncreaseButton = ({ icon }) => {
  const editor = useSlate();
  return (
    <Button
      title="Increase Column Width (+5%)"
      onMouseDown={event => {
        event.preventDefault();
        adjustColumnWidth(editor, 5);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

ColWidthIncreaseButton.propTypes = {
  icon: PropTypes.any
};

const ColWidthSelect = () => {
  const editor = useSlate();
  return (
    <select
      title="Select Column Width Percentage"
      onChange={e => {
        setColumnWidth(editor, e.target.value);
      }}
      defaultValue=""
      style={{
        height: "26px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        padding: "0 2px",
        cursor: "pointer",
        backgroundColor: "#fff",
        fontSize: "11px",
        margin: "0 2px"
      }}
    >
      <option value="" disabled>
        Col Width
      </option>
      <option value="auto">Auto</option>
      <option value="5%">5%</option>
      <option value="10%">10%</option>
      <option value="15%">15%</option>
      <option value="20%">20%</option>
      <option value="25%">25%</option>
      <option value="30%">30%</option>
      <option value="35%">35%</option>
      <option value="40%">40%</option>
      <option value="50%">50%</option>
      <option value="60%">60%</option>
      <option value="70%">70%</option>
      <option value="80%">80%</option>
      <option value="90%">90%</option>
      <option value="100%">100%</option>
    </select>
  );
};

const TableWidthSelect = () => {
  const editor = useSlate();
  return (
    <select
      title="Select Total Table Width"
      onChange={e => {
        setTableWidth(editor, e.target.value);
      }}
      defaultValue=""
      style={{
        height: "26px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        padding: "0 2px",
        cursor: "pointer",
        backgroundColor: "#fff",
        fontSize: "11px",
        margin: "0 2px"
      }}
    >
      <option value="" disabled>
        Table Width
      </option>
      <option value="100%">100%</option>
      <option value="90%">90%</option>
      <option value="80%">80%</option>
      <option value="75%">75%</option>
      <option value="60%">60%</option>
      <option value="50%">50%</option>
      <option value="auto">Auto</option>
    </select>
  );
};

const TableHeaderButton = ({ icon }) => {
  const editor = useSlate();
  return (
    <Button
      title="Toggle Bold Row (Header)"
      onMouseDown={event => {
        event.preventDefault();
        toggleTableHeader(editor);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

TableHeaderButton.propTypes = {
  icon: PropTypes.any
};

const TableColorPicker = () => {
  const editor = useSlate();
  return (
    <label
      title="Select Row Background Color"
      style={{
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        padding: "0 6px",
        height: "30px"
      }}
    >
      <input
        type="color"
        defaultValue="#E6F0F8"
        onChange={e => {
          setTableRowColor(editor, e.target.value);
        }}
        style={{
          width: "22px",
          height: "22px",
          border: "none",
          cursor: "pointer",
          backgroundColor: "transparent",
          padding: 0
        }}
      />
    </label>
  );
};

const initialValue = [
  {
    type: "paragraph",
    children: [
      {
        text: ""
      }
    ]
  }
];

export default RichTextComponent;

RichTextComponent.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.any,
  isCreate: PropTypes.bool,
  disable: PropTypes.bool
};
